import { ChevronLeftIcon, ChevronRightIcon, ScrollTextIcon } from "lucide-react"
import type * as React from "react"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router-dom"
import {
  AUDIT_OFFSET_MAX,
  AUDIT_PAGE_DEFAULT,
  type AuditEntryResponse,
} from "@steelbill/types"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { DateField } from "@/components/DateField"
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty"
import { Label } from "@/components/ui/label"
import { NativeSelect, NativeSelectOption } from "@/components/ui/native-select"
import { Skeleton } from "@/components/ui/skeleton"
import {
  auditActionKey,
  auditEntityKey,
  auditViewByKey,
  AUDIT_VIEWS,
  capabilityDelta,
  payloadFields,
} from "@/features/audit/audit-copy"
import { useAuditEntries } from "@/features/audit/use-audit"
import { istTimeOfInstant } from "@/features/messages/message-time"
import { useUsers } from "@/features/users/use-users"
import { ApiError } from "@/lib/api-error"
import { istDayOfInstant, useFormatDay } from "@/lib/dates"

/**
 * `/settings/audit` — the audit window (spec §13, §14.3), behind `audit.view`.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * A LEGALLY-RETAINED TABLE THAT NO HUMAN COULD OPEN. `audit_log` has been
 * written by every mutating path in this API since the beginning; §2.1 adds
 * every DENIED attempt on top of that; login auditing landed on 2026-08-17 —
 * sign-ins, sign-outs, refusals after a correct credential, lockouts and every
 * attempt made against a frozen account. Under GST the books must be
 * reconstructable and the trail is kept for 72 months. Until this screen,
 * nothing in the system could read a single row of it.
 *
 * The owner asked for it in as many words: "a full audit window for me — who
 * logged in when, when was what happened, and this will stay indefinitely."
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * FOUR THINGS MAKE IT A WINDOW RATHER THAN A WALL OF ROWS:
 *
 *  1. "WHO LOGGED IN WHEN" IS A FILTER, NOT A SCROLL. One tap on "People and
 *     sign-ins" (`entityType=user`, which every auth event writes), a second on
 *     a person, and the question is answered. See `AUDIT_VIEWS`.
 *
 *  2. A ROLE CHANGE SHOWS WHAT IT ACTUALLY GRANTED. The server records `gained`
 *     and `lost` capabilities on the row precisely because `ROLE_CAPABILITIES`
 *     will not be what it is today in six years — so "ACCOUNTANT → ADMIN" alone
 *     would be a fact the reader cannot evaluate. Those lists are the substance
 *     of the row and are rendered as such.
 *
 *  3. A REDACTED VALUE IS SHOWN AS `[REDACTED]`. That is the value the log
 *     holds. Nothing here reconstructs, re-derives or prettifies one, and
 *     nothing renders it as an empty cell — which would say the field was blank
 *     rather than withheld.
 *
 *  4. A ROW THIS BUILD DOES NOT UNDERSTAND STILL LISTS, showing its stored
 *     action and entity type. `action` and `entity_type` are `VarChar(64)`
 *     rather than enums because the catalogue grows; a window that hid what it
 *     could not parse would stop being evidence exactly when it mattered.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * PAGING IS `hasMore` AND THERE IS NO PAGE COUNT ANYWHERE ON THIS SCREEN. The
 * endpoint answers no `total`, deliberately — a `COUNT(*)` over a filtered
 * six-year table is an index scan of every matching row, paid on every page. So
 * there is no "page 3 of 40" to render, and estimating one would be inventing
 * the number the server refused to compute.
 *
 * THE FILTERS LIVE IN THE URL, so a narrowed window survives a reload and can be
 * sent from one phone to the other — the same construction the registers use.
 * The offset is in there too: "where I had got to" is part of where you are.
 *
 * NO FEATURE FLAG, on the route or on the API. §18.3 names "the audit write"
 * among the things nothing may be flaggable that changes, and a switch that
 * could 404 the READ is the same defect one step later: a log nobody can open is
 * indistinguishable from a log that was never written. A 404 here is a real one.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function AuditScreen(): React.JSX.Element {
  const { t } = useTranslation()
  const [params, setParams] = useSearchParams()

  const view = auditViewByKey(params.get("view"))
  /*
   * `?actor=0` IS NOT A FILTER. `AuditQuerySchema` requires `actorUserId` to be
   * positive and would answer 400, so a hand-edited or truncated URL must read
   * as "anyone" rather than as a refused request the reader cannot explain.
   * `offset`, on the same helper, legitimately IS zero — hence the extra guard
   * here rather than inside it.
   */
  const rawActor = nonNegativeInteger(params.get("actor"))
  const actor = rawActor === undefined || rawActor === 0 ? undefined : rawActor
  const from = params.get("from") ?? ""
  const to = params.get("to") ?? ""
  const offset = Math.max(0, nonNegativeInteger(params.get("offset")) ?? 0)

  const entries = useAuditEntries({
    entityType: view.entityType,
    action: view.action,
    actorUserId: actor,
    from,
    to,
    offset,
  })

  /*
   * THE PEOPLE, FOR THE "WHO" FILTER. `actorUserId` is a number on the wire and
   * a row carries `actorName`, so the LIST is only needed to offer names to pick
   * from. `audit.view` and `user.manage` are both on §2.1's Administration row,
   * so anyone who can open this screen can read it — but the query is allowed to
   * fail without taking the screen with it: the log is the point, the picker is
   * a convenience, and a 403 here must not hide the rows.
   */
  const people = useUsers()

  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(params)
    if (value === "") next.delete(key)
    else next.set(key, value)
    // A changed filter means a different list, so the old offset points at
    // nothing — leaving it would open the new question on its fourth page.
    next.delete("offset")
    setParams(next, { replace: true })
  }

  const goToOffset = (nextOffset: number) => {
    const next = new URLSearchParams(params)
    if (nextOffset <= 0) next.delete("offset")
    else next.set("offset", String(nextOffset))
    setParams(next)
  }

  const rows = entries.data?.entries ?? []
  const hasMore = entries.data?.hasMore ?? false
  const nextOffset = offset + AUDIT_PAGE_DEFAULT
  /*
   * THE OFFSET CAP IS THE SCHEMA'S (10,000) AND IT ANSWERS 400. Stopping here
   * rather than sending the request means the reader gets the sentence about
   * narrowing the range instead of a refusal they cannot act on — and the
   * refusal is right: deep offset paging over an append-only log is a scan whose
   * cost grows with how bored the reader is, and `from`/`to` are indexed.
   */
  const cappedOut = nextOffset > AUDIT_OFFSET_MAX

  return (
    <div className="flex w-full flex-col gap-4 p-4 md:gap-6 md:p-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">{t("audit.title")}</h1>
        <p className="text-muted-foreground text-sm">{t("audit.screenHelp")}</p>
      </header>

      <div className="grid gap-3 md:grid-cols-4">
        <div className="flex flex-col gap-1.5 md:col-span-2">
          <Label htmlFor="auditView">{t("audit.viewLabel")}</Label>
          <NativeSelect
            id="auditView"
            className="w-full"
            value={view.key}
            onChange={(event) => {
              setFilter("view", event.target.value === "all" ? "" : event.target.value)
            }}
          >
            {AUDIT_VIEWS.map((option) => (
              <NativeSelectOption key={option.key} value={option.key}>
                {t(`audit.view.${option.key}`)}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </div>

        <div className="flex flex-col gap-1.5 md:col-span-2">
          <Label htmlFor="auditActor">{t("audit.actorLabel")}</Label>
          <NativeSelect
            id="auditActor"
            className="w-full"
            value={actor === undefined ? "" : String(actor)}
            disabled={people.data === undefined}
            onChange={(event) => {
              setFilter("actor", event.target.value)
            }}
          >
            <NativeSelectOption value="">{t("audit.anyone")}</NativeSelectOption>
            {(people.data?.users ?? []).map((person) => (
              <NativeSelectOption key={person.id} value={String(person.id)}>
                {person.displayName}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </div>

        {/*
          * THE APP'S OWN CALENDAR, NEVER `<input type="date">`. The native
          * control renders in the BROWSER's locale and calendar rather than the
          * app's, so a Marathi screen opens an English picker and Chrome, Safari
          * and Android each draw a different thing — the registers were swept
          * off it on 2026-08-15 and this screen must not reintroduce it.
          * `clearable`, because on a filter "no date" is a real answer.
          */}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="auditFrom">{t("audit.from")}</Label>
          <DateField
            id="auditFrom"
            clearable
            value={from}
            ariaLabel={t("audit.from")}
            onChange={(value) => {
              setFilter("from", value)
            }}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="auditTo">{t("audit.to")}</Label>
          <DateField
            id="auditTo"
            clearable
            value={to}
            ariaLabel={t("audit.to")}
            onChange={(value) => {
              setFilter("to", value)
            }}
          />
        </div>

        <div className="flex items-end md:col-span-2">
          <Button
            variant="outline"
            onClick={() => {
              setParams(new URLSearchParams(), { replace: true })
            }}
          >
            {t("audit.clearFilters")}
          </Button>
        </div>
      </div>

      {/*
        * BOTH ENDS ARE INCLUSIVE CALENDAR DAYS IN IST, resolved on the server —
        * `to=2026-08-31` includes everything up to 23:59:59.999 that evening.
        * Said here because the alternative reading (an exclusive upper bound)
        * silently loses a whole day of a legally-retained log.
        */}
      <p className="text-muted-foreground text-xs">{t("audit.dateHelp")}</p>

      {entries.isPending ? (
        <Skeleton className="h-96 w-full" />
      ) : entries.isError ? (
        <Alert variant="destructive">
          <AlertTitle>{t("err.couldNotLoad")}</AlertTitle>
          <AlertDescription className="flex flex-col items-start gap-3">
            <span>
              {entries.error instanceof ApiError
                ? entries.error.message
                : t("err.somethingWentWrong")}
            </span>
            <Button
              variant="outline"
              onClick={() => {
                void entries.refetch()
              }}
            >
              {t("btn.retry")}
            </Button>
          </AlertDescription>
        </Alert>
      ) : rows.length === 0 ? (
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <ScrollTextIcon />
            </EmptyMedia>
            <EmptyTitle>{t("audit.emptyTitle")}</EmptyTitle>
            <EmptyDescription>{t("audit.emptyHelp")}</EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <Card className="overflow-hidden py-0">
          <ul className="divide-border divide-y">
            {/* Newest first — the server's order, never re-sorted here. */}
            {rows.map((entry) => (
              <AuditRow key={entry.id} entry={entry} />
            ))}
          </ul>
        </Card>
      )}

      {/*
        * PAGING ON `hasMore`. "Older" is the only forward direction on a
        * newest-first log, and it disappears — rather than greying out with a
        * page number beside it — the moment the server says there is nothing
        * behind this page.
        */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-muted-foreground text-sm">
          {/*
            * THE ROWS ON THIS PAGE AND WHERE THEY START — never "page 3 of 40",
            * which cannot be computed without the `total` this endpoint refuses
            * to run. The placeholder is `shown` rather than `count` on purpose:
            * `count` is i18next's plural trigger, and a plural form here would
            * imply a total the screen does not have.
            */}
          {rows.length === 0
            ? ""
            : t("audit.showing", { shown: rows.length, first: offset + 1 })}
        </p>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            disabled={offset === 0 || entries.isFetching}
            onClick={() => {
              goToOffset(Math.max(0, offset - AUDIT_PAGE_DEFAULT))
            }}
          >
            <ChevronLeftIcon />
            {t("audit.newer")}
          </Button>
          <Button
            variant="outline"
            disabled={!hasMore || cappedOut || entries.isFetching}
            onClick={() => {
              goToOffset(nextOffset)
            }}
          >
            {t("audit.older")}
            <ChevronRightIcon />
          </Button>
        </div>
      </div>

      {hasMore && cappedOut ? (
        <Alert>
          <AlertTitle>{t("audit.cappedTitle")}</AlertTitle>
          <AlertDescription>{t("audit.cappedHelp")}</AlertDescription>
        </Alert>
      ) : null}
    </div>
  )
}

/** `?offset=50` → 50; anything malformed → undefined. Deny-by-default. */
function nonNegativeInteger(raw: string | null): number | undefined {
  if (raw === null || raw.trim() === "") return undefined
  const value = Number(raw)
  if (!Number.isInteger(value) || value < 0) return undefined
  return value
}

/* -------------------------------------------------------------------------- */
/* One row                                                                     */
/* -------------------------------------------------------------------------- */

function AuditRow({ entry }: { entry: AuditEntryResponse }): React.JSX.Element {
  const { t } = useTranslation()
  const formatDay = useFormatDay()

  const actionKey = auditActionKey(entry.action)
  const entityKey = auditEntityKey(entry.entityType)
  const delta = capabilityDelta(entry.after)

  /*
   * `createdAt` IS AN ISO INSTANT, NOT A CALENDAR DAY. Slicing it to ten
   * characters takes the UTC day and prints it as an IST one, so every row
   * written between 00:00 and 05:30 IST would report yesterday — and the whole
   * point of this screen is when something happened.
   */
  const when = t("audit.whenAt", {
    day: formatDay(istDayOfInstant(entry.createdAt)),
    time: istTimeOfInstant(entry.createdAt),
  })

  const before = payloadFields(entry.before)
  const after = payloadFields(entry.after)

  return (
    <li className="flex flex-col gap-2 p-4" data-testid="audit-row">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="text-sm font-semibold">
          {/*
            * THE ACTION, NAMED WHERE THIS BUILD KNOWS THE NAME AND STORED
            * VERBATIM WHERE IT DOES NOT. A row written by a later version of
            * this app lists with its own action string rather than vanishing.
            */}
          {actionKey === null ? entry.action : t(actionKey)}
        </span>
        {actionKey === null ? (
          <Badge variant="outline">{t("audit.unknownAction")}</Badge>
        ) : null}
        <span className="text-muted-foreground text-sm">{when}</span>
      </div>

      <p className="text-muted-foreground text-sm">
        {/*
          * THE ACTOR'S NAME AND ROLE AS THEY ARE **TODAY**, joined from `users`
          * — never what they were when the row was written. A renamed or
          * departed person is the same person and the id beside them is the
          * stable identity. `actorRole` in particular must not be read as "the
          * role they held then": a role CHANGE is itself an audited event, and
          * the log answers that question from its own rows.
          */}
        {entry.actorUserId === null
          ? t("audit.bySystem")
          : t("audit.byPerson", {
              name: entry.actorName ?? t("audit.unknownPerson"),
              role:
                entry.actorRole === null
                  ? t("audit.unknownRole")
                  : t(`role.${entry.actorRole.toLowerCase()}`),
              id: entry.actorUserId,
            })}
        {" · "}
        {entityKey === null
          ? t("audit.onRawEntity", { entityType: entry.entityType, id: entry.entityId })
          : t("audit.onEntity", { entity: t(entityKey), id: entry.entityId })}
      </p>

      {/*
        * WHAT A ROLE CHANGE ACTUALLY GRANTED. Rendered above the raw payload
        * because it is the point of the row, not a detail of it — see
        * `capabilityDelta`.
        */}
      {delta === null ? null : (
        <div className="flex flex-col gap-1 text-sm">
          {delta.gained.length === 0 ? null : (
            <p>
              <span className="font-medium">{t("audit.gained")}</span>{" "}
              <span className="text-muted-foreground">{delta.gained.join(", ")}</span>
            </p>
          )}
          {delta.lost.length === 0 ? null : (
            <p>
              <span className="font-medium">{t("audit.lost")}</span>{" "}
              <span className="text-muted-foreground">{delta.lost.join(", ")}</span>
            </p>
          )}
          {delta.gained.length === 0 && delta.lost.length === 0 ? (
            <p className="text-muted-foreground">{t("audit.noCapabilityChange")}</p>
          ) : null}
        </div>
      )}

      {before.length === 0 && after.length === 0 ? null : (
        <details className="text-sm">
          <summary className="text-muted-foreground cursor-pointer">
            {t("audit.showPayload")}
          </summary>
          <div className="mt-2 grid gap-3 md:grid-cols-2">
            <PayloadBlock title={t("audit.before")} fields={before} />
            <PayloadBlock title={t("audit.after")} fields={after} />
          </div>
        </details>
      )}
    </li>
  )
}

/**
 * One side of the row's payload, as stored.
 *
 * VALUES ARE PRINTED AS THEY ARE HELD, INCLUDING `[REDACTED]`. That literal is
 * what `redactForAudit` wrote, and it is the honest answer to "what does this row
 * say" — showing a blank instead would claim the field was empty, and showing
 * anything reconstructed would be this screen inventing evidence.
 *
 * It scrolls sideways in its own box rather than pushing the page wide: a
 * customer snapshot is a long line, and a horizontally scrolling BODY is how a
 * table becomes unreadable on the phone the son actually carries.
 */
function PayloadBlock({
  title,
  fields,
}: {
  title: string
  fields: readonly { key: string; value: string }[]
}): React.JSX.Element | null {
  if (fields.length === 0) return null

  return (
    <div className="flex flex-col gap-1">
      <p className="text-xs font-medium">{title}</p>
      <div className="bg-muted/40 overflow-x-auto rounded-md p-2">
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs">
          {fields.map((field) => (
            <div key={`${field.key}:${field.value}`} className="contents">
              <dt className="text-muted-foreground font-mono whitespace-nowrap">{field.key}</dt>
              <dd className="font-mono break-all">{field.value}</dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  )
}
